import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';
import {isString, noop} from 'lodash';
import {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react';
import {z as r} from 'zod';

import {FeedResultsTypeEnum} from '../../page/main/my-companies/feed/common/results/feed-results-const';
import {useDomainConfig} from '../../provider/domain-config/domain-config-hook';
import {ShortLocaleNameEnum} from '../../provider/locale/locale-context-type';
import {useLocale} from '../../provider/locale/locale-hook';
import {Locale} from '../../provider/locale/localization';
import {useMessage} from '../../provider/message/message-hook';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {useUser} from '../../provider/user/user-hook';
import {postAndDeserialize, postNoContent} from '../../util/api-adapter';
import {ApiError} from '../../util/error';
import {UserType} from '../user/user-type';

import {companiesImportUrl} from './companies-import-const';
import {CompaniesImportResultType, fetchCompaniesImportValidationResult} from './companies-import-result';
import {getCompaniesImportErrorCodesUrl} from './companies-import-result-error-codes';
import {CompaniesImportTypeEnum} from './companies-import-type';

const DELAY_SECONDS = 5;

const companiesImportValidationSchema = r.object({
    tempId: r.string(),
    importUrlId: r.number().optional(),
});

type CompaniesImportValidationType = r.infer<typeof companiesImportValidationSchema>;

export type CompaniesValidationOptionsType = {
    importType: CompaniesImportTypeEnum;
    link?: string | null;
    feedFileTempId?: string;
    feedFileName?: string;
    brandId?: number;
    allowedBrands?: Array<{brandId: number; brandCode: string}>;
    // Recipients the validation results are e-mailed to (validator + import).
    // Authorized flows (authorized_validate / import_url) deliver them via the
    // dedicated `send_to_emails` endpoint once validation finishes; the public
    // validator has no such endpoint, so they go straight into the `validate/`
    // request as `emails_for_send_results`. See DEV-14383 / DEV-14349.
    emailsForSendResults?: Array<string>;
};

function postValidateCompany(
    options: CompaniesValidationOptionsType & {user: UserType | null; shortLocaleName: ShortLocaleNameEnum}
): Promise<CompaniesImportValidationType> {
    const {feedFileName, emailsForSendResults, user, shortLocaleName, ...urlOptions} = options;

    const url = `${companiesImportUrl}/validate/`;

    if (user) {
        // Authorized: recipients are handled after completion via send_to_emails, not here.
        return postAndDeserialize(
            `${companiesImportUrl}/authorized_validate/`,
            companiesImportValidationSchema,
            urlOptions
        );
    }

    return postAndDeserialize(
        url,
        companiesImportValidationSchema,
        emailsForSendResults?.length ? {...urlOptions, emailsForSendResults} : urlOptions,
        {unauthorized: true, shortLocaleName}
    );
}

function postImportCompany(options: CompaniesValidationOptionsType): Promise<CompaniesImportValidationType> {
    // Recipients are handled after completion via send_to_emails, not in this request.
    const {feedFileName, emailsForSendResults, ...urlOptions} = options;

    const url = `${companiesImportUrl}/import_url/`;

    return postAndDeserialize(url, companiesImportValidationSchema, urlOptions);
}

// Dedicated endpoint (DEV-14349): e-mails the validation results file. Called only
// after validation has finished (so the result is ready and the endpoint answers 204,
// not 202). Trailing slash is required. Empty `emails` → the user's default e-mail.
function sendValidationResultToEmails(id: string, emails: Array<string>): Promise<void> {
    return postNoContent(`${companiesImportUrl}/validate_result/${id}/send_to_emails/`, {emails});
}

// Authorized flow, validation finished, recipients chosen, not sent yet.
function shouldSendResultsToEmails(
    percent: number,
    user: UserType | null,
    emails: Array<string> | undefined,
    alreadySent: boolean
): boolean {
    return percent === 100 && Boolean(user) && Boolean(emails?.length) && !alreadySent;
}

export type UseCompaniesImportValidationHookType = UseMutationResult<
    CompaniesImportValidationType,
    unknown,
    CompaniesValidationOptionsType
> & {
    id: string | null;
    importUrlId: number | null;
    result: CompaniesImportResultType | null;
    percent: number;
    remainingRelativeTime: string | null;
    source: CompaniesValidationOptionsType | null;
    isDisabled: boolean;
    setIsDisabled: Dispatch<SetStateAction<boolean>>;
    isCancelled: boolean;
    setIsCancelled: Dispatch<SetStateAction<boolean>>;
};

// eslint-disable-next-line max-statements
export function useCompaniesImportValidation(type: FeedResultsTypeEnum): UseCompaniesImportValidationHookType {
    const [percent, setPercent] = useState(0);
    const [isDisabled, setIsDisabled] = useState(false);
    const [isCancelled, setIsCancelled] = useState(false);
    const [result, setResult] = useState<CompaniesImportResultType | null>(null);
    const [source, setSource] = useState<CompaniesValidationOptionsType | null>(null);

    const timeout = useRef<NodeJS.Timeout>();
    const emailsSentRef = useRef(false);
    const queryClient = useQueryClient();
    const {feedTypeLabels} = useDomainConfig();
    const {user} = useUser();
    const {snackbar} = useSnackbar();
    const {message} = useMessage();
    const {shortLocaleName} = useLocale();

    function reset() {
        setPercent(0);
        setResult(null);
        setSource(null);
        setIsDisabled(false);
        emailsSentRef.current = false;
    }

    // Authorized flows only: e-mail the results once validation is done.
    async function sendResultsToEmails(id: string, emails: Array<string>): Promise<void> {
        try {
            await sendValidationResultToEmails(id, emails);

            snackbar.success({
                message: <Locale stringKey="FEED__VALIDATION__EMAILS__SENT__TITLE" />,
                description: <Locale stringKey="FEED__VALIDATION__EMAILS__SENT__DESCRIPTION" />,
            });
        } catch {
            snackbar.error(<Locale stringKey="FEED__VALIDATION__EMAILS__SENT__ERROR" />);
        }
    }

    function showSuccess(newType?: CompaniesImportTypeEnum) {
        if (type === FeedResultsTypeEnum.Import) {
            setIsDisabled(true);
        }

        snackbar.success({
            message: (
                <Locale
                    stringKey={
                        type === FeedResultsTypeEnum.Import
                            ? 'FEED__IMPORT__SUCCESS__TITLE'
                            : 'FEED__VALIDATION__SUCCESS__TITLE'
                    }
                />
            ),
            description: (
                <Locale
                    stringKey={
                        type === FeedResultsTypeEnum.Import
                            ? 'FEED__IMPORT__SUCCESS__DESCRIPTION'
                            : 'FEED__VALIDATION__SUCCESS__DESCRIPTION'
                    }
                    valueMap={{
                        format: newType && feedTypeLabels ? feedTypeLabels[newType] : '',
                    }}
                />
            ),
        });
    }

    async function updateResult(
        id?: string,
        importType?: CompaniesImportTypeEnum,
        emailsForSendResults?: Array<string>
    ) {
        if (!id || percent === 100) {
            return;
        }

        try {
            const {validationResult} = await fetchCompaniesImportValidationResult(id);
            const newPercent = Math.round((validationResult.processedCount / validationResult.all) * 100);
            const newType = importType || source?.importType;

            setResult(validationResult);
            setPercent(newPercent);

            // Authorized flows: once validation is done, e-mail the results via the dedicated
            // endpoint. The public validator sends recipients within the validate request instead.
            if (shouldSendResultsToEmails(newPercent, user, emailsForSendResults, emailsSentRef.current)) {
                emailsSentRef.current = true;
                sendResultsToEmails(id, emailsForSendResults ?? []);
            }

            if (newPercent === 100 && !validationResult.foreignError && !validationResult.internalError) {
                showSuccess(newType);
            } else {
                timeout.current = setTimeout(
                    () => updateResult(id, importType, emailsForSendResults),
                    DELAY_SECONDS * 1000
                );
            }

            queryClient.invalidateQueries({queryKey: [getCompaniesImportErrorCodesUrl(id)]});
        } catch {
            timeout.current = setTimeout(
                () => updateResult(id, importType, emailsForSendResults),
                DELAY_SECONDS * 1000
            );
        }
    }

    const mutation = useMutation({
        mutationFn: (options: CompaniesValidationOptionsType) =>
            type === FeedResultsTypeEnum.Validation
                ? postValidateCompany({...options, user, shortLocaleName})
                : postImportCompany(options),
        onMutate: () => {
            setIsCancelled(false);
        },
        onSuccess: ({tempId}, options) => {
            updateResult(tempId, options.importType, options.emailsForSendResults);
            setSource(options);
        },
        onError: (error, options) => {
            if (error instanceof ApiError && Array.isArray(error.jsonData) && error.jsonData.every(isString)) {
                message.error(error.jsonData.join(', '));
                reset();
            } else {
                setSource(options);
            }
        },
    });

    const {data} = mutation;

    useEffect(() => {
        if (!data?.tempId) {
            return noop;
        }

        return () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
        };
    }, [data?.tempId]);

    return {
        ...mutation,
        id: data?.tempId ?? null,
        importUrlId: data?.importUrlId ?? null,
        result,
        percent,
        remainingRelativeTime: null,
        source,
        isDisabled,
        isCancelled,
        setIsDisabled,
        setIsCancelled,
        reset: () => {
            mutation.reset();
            reset();
        },
    };
}
